/**
 * AI Assistant Chat
 * 
 * The core chat UI used by both dashboard card and sidebar panel.
 * Shared state and logic, different visual presentations.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Bot, X, Trash2, AlertCircle, Shield } from 'lucide-react';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { SuggestedPrompts } from './SuggestedPrompts';
import { StreamingMessage } from './StreamingMessage';
import { useStreamingChat } from '../../hooks/useStreamingChat';
import { useIncidentAssistantSafe } from '../../context/IncidentAssistantContext';
import { useAIAssistantUI } from '../../context/AIAssistantUIContext';
import { useUser } from '../../context/UserContext';
import {
  getOrCreateConversation,
  addMessage,
  clearConversation,
  initAssistantStorage,
  type ChatMessage as StoredMessage,
  type Conversation,
} from '../../lib/assistantStorage';

const DEFAULT_PROMPTS = [
  "What's a phishing attack?",
  "Explain brute force attacks",
  "How do I respond to ransomware?",
  "What is MITRE ATT&CK?",
];

const INCIDENT_PROMPTS = [
  'What is this attack trying to achieve?',
  'What should I investigate next?',
  'How do I contain this threat?',
  'Explain the MITRE techniques',
];

interface AIAssistantChatProps {
  /** 'card' for dashboard, 'panel' for sidebar */
  mode: 'card' | 'panel';
}

export function AIAssistantChat({ mode }: AIAssistantChatProps) {
  const params = useParams<{ tenantId?: string; incidentId?: string }>();
  const { isDashboard, closePanel } = useAIAssistantUI();
  const { swaUser } = useUser();
  
  const assistantContext = useIncidentAssistantSafe();
  const hasIncidentContext = !isDashboard && assistantContext?.incidentData && params.tenantId && params.incidentId;

  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<StoredMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [localSuggestions, setLocalSuggestions] = useState<string[]>(DEFAULT_PROMPTS);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const {
    isStreaming,
    isTyping,
    displayedContent,
    error: streamError,
    suggestedFollowUps,
    streamMessage,
    streamIncidentMessage,
    resetStream,
    skipToEnd,
  } = useStreamingChat();
  
  // Track pending response that hasn't been committed to messages yet
  const [pendingResponse, setPendingResponse] = useState<string | null>(null);

  // Initialize storage and load conversation scoped to current user
  useEffect(() => {
    if (!swaUser?.userId) return;
    async function init() {
      await initAssistantStorage();
      
      const context = hasIncidentContext
        ? { tenantId: params.tenantId!, incidentId: params.incidentId! }
        : 'dashboard';
      
      const conv = await getOrCreateConversation(context, swaUser!.userId);
      setConversation(conv);
      setMessages(conv.messages);
    }
    init();
  }, [hasIncidentContext, params.tenantId, params.incidentId, swaUser]);

  useEffect(() => {
    setLocalSuggestions(hasIncidentContext ? INCIDENT_PROMPTS : DEFAULT_PROMPTS);
  }, [hasIncidentContext]);

  useEffect(() => {
    if (suggestedFollowUps.length > 0) {
      setLocalSuggestions(suggestedFollowUps);
    }
  }, [suggestedFollowUps]);

  // Scroll within container only (not the page)
  useEffect(() => {
    const container = messagesEndRef.current?.closest('[data-scroll-container]');
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, displayedContent]);

  const handleSend = useCallback(async (messageContent: string) => {
    if (!conversation) return;

    setError(null);
    resetStream();
    setPendingResponse(null);

    const userMessage: StoredMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: messageContent,
      timestamp: Date.now(),
    };
    setMessages(prev => [...prev, userMessage]);
    await addMessage(conversation.id, 'user', messageContent);

    try {
      const history = messages.map(m => ({
        role: m.role as 'user' | 'assistant',
        content: m.content,
      }));

      let fullResponse: string;

      if (hasIncidentContext && params.tenantId && params.incidentId && assistantContext) {
        const context = assistantContext.buildAIContext();
        fullResponse = await streamIncidentMessage(
          params.tenantId,
          params.incidentId,
          messageContent,
          history,
          context
        );
      } else {
        fullResponse = await streamMessage(messageContent, history);
      }

      // Store the response but don't add to messages yet - wait for typewriter
      if (fullResponse) {
        setPendingResponse(fullResponse);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  }, [conversation, messages, hasIncidentContext, params.tenantId, params.incidentId, assistantContext, streamMessage, streamIncidentMessage, resetStream]);
  
  // Commit pending response to messages when typewriter finishes
  useEffect(() => {
    async function commitPendingResponse() {
      if (pendingResponse && !isTyping && !isStreaming && conversation) {
        const assistantMessage = await addMessage(
          conversation.id,
          'assistant',
          pendingResponse
        );
        setMessages(prev => [...prev, assistantMessage]);
        setPendingResponse(null);
      }
    }
    commitPendingResponse();
  }, [pendingResponse, isTyping, isStreaming, conversation]);

  const handleClear = useCallback(async () => {
    if (!conversation) return;
    await clearConversation(conversation.id);
    setMessages([]);
    setPendingResponse(null);
    setLocalSuggestions(hasIncidentContext ? INCIDENT_PROMPTS : DEFAULT_PROMPTS);
    setError(null);
    resetStream();
  }, [conversation, hasIncidentContext, resetStream]);

  const displayError = error || streamError;
  const isActive = isStreaming || isTyping || pendingResponse !== null;
  const showStreamingMessage = isStreaming || isTyping || (pendingResponse !== null && displayedContent);
  const isCard = mode === 'card';

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-default bg-bg-tertiary/50 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="p-1.5 rounded-lg bg-accent-blue/15 flex-shrink-0">
            <Bot className="w-4 h-4 text-accent-blue" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-medium text-text-primary flex items-center gap-2">
              AI Assistant
              {hasIncidentContext && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                  <Shield className="w-3 h-3" />
                  Incident
                </span>
              )}
            </h3>
            <p className="text-xs text-text-secondary truncate">
              {hasIncidentContext
                ? assistantContext?.incidentData?.incident?.title?.slice(0, 25) + '...' || 'Analyzing...'
                : 'Ask about security topics'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {messages.length > 0 && (
            <button
              onClick={handleClear}
              className="p-1.5 rounded hover:bg-bg-tertiary text-text-muted hover:text-text-secondary transition-colors"
              title="Clear conversation"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {!isCard && (
            <button
              onClick={closePanel}
              className="p-1.5 rounded hover:bg-bg-tertiary text-text-muted hover:text-text-secondary transition-colors"
              title="Close (⌘K)"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4" data-scroll-container>
        {messages.length === 0 && !isActive ? (
          <div className="text-center py-6">
            <div className="relative mx-auto w-14 h-14 mb-3">
              <div className="absolute inset-0 bg-accent-blue/15 rounded-full" />
              <Bot className="absolute inset-0 m-auto w-7 h-7 text-accent-blue" />
            </div>
            <p className="text-sm text-text-secondary mb-1 font-medium">
              {hasIncidentContext ? 'I have context on this incident' : 'Hi! How can I help?'}
            </p>
            <p className="text-xs text-text-secondary mb-3 max-w-[260px] mx-auto">
              {hasIncidentContext
                ? 'Ask about this incident and how to respond.'
                : 'Ask about security concepts or incidents.'}
            </p>
            <SuggestedPrompts
              prompts={localSuggestions.slice(0, isCard ? 3 : 4)}
              onSelect={handleSend}
              disabled={isActive}
            />
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <ChatMessage
                key={msg.id}
                role={msg.role}
                content={msg.content}
                timestamp={msg.timestamp}
              />
            ))}

            {showStreamingMessage && (
              <StreamingMessage
                content={displayedContent}
                isTyping={isTyping}
                isStreaming={isStreaming}
                onSkip={skipToEnd}
              />
            )}

            <div ref={messagesEndRef} />
          </>
        )}

        {displayError && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{displayError}</span>
          </div>
        )}
      </div>

      {/* Suggested prompts when there are messages */}
      {messages.length > 0 && !isActive && (
        <div className="px-4 pb-2 flex-shrink-0">
          <SuggestedPrompts
            prompts={localSuggestions.slice(0, 2)}
            onSelect={handleSend}
            disabled={isActive}
          />
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-border-default bg-bg-tertiary/50 flex-shrink-0">
        <ChatInput
          onSend={handleSend}
          isLoading={isActive}
          placeholder={hasIncidentContext ? 'Ask about this incident...' : 'Ask me anything...'}
        />
        {!isCard && (
          <p className="text-xs text-text-muted mt-2 text-center">
            <kbd className="px-1 py-0.5 bg-bg-tertiary rounded text-xs">⌘K</kbd> to toggle
          </p>
        )}
      </div>
    </div>
  );
}
